<script setup lang="ts">
import { Bell, Users, Check, ClipboardList, ChevronRight } from 'lucide-vue-next'
import { EntityIcon, Palette, type PaletteHue } from '@/design-system/components'

/** One prepared timeline row (built in Today.vue from normalised schedule items). */
export interface TimelineRow {
  id: string
  kind: 'lesson' | 'event'
  hue: PaletteHue
  start: string
  end: string | null
  className: string
  title: string
  room?: string | null
  topic?: string | null
  /** Lesson-only; undefined when the backend hasn't supplied a plan flag. */
  planReady?: boolean
  isNext: boolean
  classId?: string
}

defineProps<{ rows: TimelineRow[] }>()
const emit = defineEmits<{ openClass: [classId: string] }>()

const indigo = Palette.indigo
</script>

<template>
  <div class="timeline">
    <div v-for="row in rows" :key="row.id" class="trow">
      <!-- time gutter -->
      <div class="trow__gutter">
        <div class="trow__start">{{ row.start }}</div>
        <div v-if="row.end" class="trow__end">{{ row.end }}</div>
      </div>

      <!-- connector -->
      <div class="trow__connector">
        <span class="trow__line" />
        <span
          class="trow__dot"
          :style="{ background: row.kind === 'event' ? indigo.solid : row.hue.solid }"
        />
      </div>

      <!-- card -->
      <button
        type="button"
        class="trow__card"
        :class="{ 'trow__card--next': row.isNext, 'trow__card--static': !row.classId }"
        :style="row.isNext ? { boxShadow: `inset 0 0 0 1px ${row.hue.solid}, 0 1px 2px rgba(15,15,20,0.04)` } : undefined"
        :disabled="!row.classId"
        @click="row.classId && emit('openClass', row.classId)"
      >
        <EntityIcon
          v-if="row.kind === 'lesson'"
          :icon="Users"
          :palette="row.hue"
          :size="38"
          :corner-radius="11"
        />
        <span v-else class="trow__event-tile">
          <Bell :size="18" />
        </span>

        <div class="trow__body">
          <div class="trow__head">
            <span v-if="row.kind === 'lesson'" class="trow__class">{{ row.className }}</span>
            <span v-else class="trow__chip">
              <span class="trow__chip-dot" />
              EVENT
            </span>
            <span
              v-if="row.isNext"
              class="trow__next"
              :style="{ color: row.hue.ink, background: row.hue.soft }"
            >
              NEXT
            </span>
          </div>
          <div class="trow__title" :class="{ 'trow__title--event': row.kind === 'event' }">
            {{ row.title }}
          </div>
          <div v-if="row.room || row.topic" class="trow__meta">
            {{ row.room }}{{ row.room && row.topic ? ' · ' : '' }}{{ row.topic }}
          </div>
        </div>

        <div v-if="row.kind === 'lesson'" class="trow__status">
          <span v-if="row.planReady === true" class="trow__plan trow__plan--ok">
            <Check :size="13" /> Plan ready
          </span>
          <span v-else-if="row.planReady === false" class="trow__plan trow__plan--no">
            <ClipboardList :size="13" /> No plan
          </span>
          <ChevronRight :size="15" class="trow__chev" />
        </div>
      </button>
    </div>
  </div>
</template>

<style scoped>
.timeline {
  font-family: var(--font-sans);
}
.trow {
  display: flex;
  gap: 16px;
  align-items: stretch;
}
/* gutter */
.trow__gutter {
  width: 58px;
  flex-shrink: 0;
  text-align: right;
  padding-top: 16px;
}
.trow__start {
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.2px;
  color: var(--text-primary);
}
.trow__end {
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
  font-size: 12px;
  color: var(--ink-400);
  margin-top: 2px;
}
/* connector */
.trow__connector {
  position: relative;
  width: 14px;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
}
.trow__line {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  background: var(--hairline);
}
.trow__dot {
  position: absolute;
  top: 20px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  box-shadow: 0 0 0 3px var(--surface);
}
/* card */
.trow__card {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 12px;
  padding: 14px 16px;
  border: 0;
  border-radius: var(--radius-lg);
  background: var(--surface);
  box-shadow: var(--hairline-border);
  text-align: left;
  cursor: pointer;
  font-family: var(--font-sans);
}
.trow__card--static {
  cursor: default;
}
.trow__event-tile {
  width: 38px;
  height: 38px;
  flex-shrink: 0;
  border-radius: 11px;
  background: var(--palette-indigo-soft);
  color: var(--palette-indigo-ink);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.trow__body {
  flex: 1;
  min-width: 0;
}
.trow__head {
  display: flex;
  align-items: center;
  gap: 9px;
  margin-bottom: 3px;
}
.trow__class {
  font-size: 14.5px;
  font-weight: 600;
  letter-spacing: -0.1px;
  color: var(--text-primary);
}
.trow__chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 8px 2px 6px;
  border-radius: var(--radius-sm);
  background: var(--palette-indigo-soft);
  color: var(--palette-indigo-ink);
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.6px;
}
.trow__chip-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--palette-indigo-solid);
}
.trow__next {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  padding: 2px 7px;
  border-radius: 5px;
}
.trow__title {
  font-size: 14px;
  color: var(--ink-700);
}
.trow__title--event {
  color: var(--text-primary);
  font-weight: 600;
}
.trow__meta {
  font-size: 12.5px;
  color: var(--ink-500);
  margin-top: 2px;
}
.trow__status {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}
.trow__plan {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 500;
}
.trow__plan--ok {
  color: var(--success-ink);
}
.trow__plan--no {
  color: var(--warning-ink);
}
.trow__chev {
  color: var(--ink-300);
}
</style>
